import kebabCase from 'lodash/kebabCase';
import VideoEmbed from '@rmpenterprise/microsite-youtube-embed';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faLevelDownAlt } from '@fortawesome/free-solid-svg-icons';
import HintMessage from '../partials/dynamic-field-hint-message.vue';
import ErrorMessage from '../partials/dynamic-field-error-message.vue';
import textOnly from '../layouts/text-only.vue';
import imageLeft from '../layouts/image-left.vue';
import imageRight from '../layouts/image-right.vue';
import mediaMiddle from '../layouts/media-middle.vue';
import backgroundImage from '../layouts/background-image.vue';
import MediaMiddlePartial from '../layouts/media-middle-partial.vue';

export default {
  model: {
    prop: 'fieldValue',
    event: 'change',
  },

  components: {
    HintMessage,
    ErrorMessage,
    textOnly,
    imageLeft,
    imageRight,
    mediaMiddle,
    backgroundImage,
    VideoEmbed,
    MediaMiddlePartial,
    FontAwesomeIcon,
  },

  props: {
    fieldValue: {
      type: [String, Number, Boolean, Object, Array, File],
      default: null,
    },
    question: {
      type: Object,
      default: () => {},
    },
    hint: {
      default: null,
      type: Object,
    },
    error: {
      default() {
        return {
          status: '',
          message: '',
          errors: [],
        };
      },
      type: Object,
    },
    autoFocusFirstField: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isBlurred: false,
      enterIcon: faLevelDownAlt,
      disableNextButton: false,
    };
  },

  computed: {
    options() {
      if (!this.question.options) return null;

      return this.question.options.map((option) => {
        if ((typeof option === 'object' && option !== null)) return option;

        return {
          id: option,
          label: option,
        };
      });
    },
    fieldQuestionId() {
      return `field-${this.question.id}`;
    },
    fieldHasError() {
      if (this.error.invalid === undefined) return false;

      if (this.error.invalid && this.isBlurred) return true;

      return this.error.errors.some((error) => {
        return !error.status && this.manualValidate;
      });
    },
    layout() {
      return this.question.layout;
    },
    isNextButtonDisabled() {
      return this.disableNextButton;
    },
    isTouchDevice() {
      return (('ontouchstart' in window)
        || (navigator.maxTouchPoints > 0)
        || (navigator.msMaxTouchPoints > 0));
    },
  },

  mounted() {
    this.setInitialValue();
    this.autoFocusField();
    this.bindEventListeners();
  },

  beforeDestroy() {
    this.unbindEventListeners();
  },

  methods: {
    setInitialValue() {
      if (this.question.initialVal) {
        this.$emit('change', this.question.initialVal);
      }
    },
    autoFocusField() {
      if (!this.autoFocusFirstField) return;

      if (this.isTouchDevice) {
        this.$refs[this.generateSafeId(this.fieldQuestionId, 'wrapper')].scrollIntoView();
        return;
      }

      let target = this.$refs['auto-focus-target'];

      if (target === undefined) return;

      if (Array.isArray(this.$refs['auto-focus-target'])) {
        target = this.$refs['auto-focus-target'][0]; // eslint-disable-line
      }

      target.focus();
    },

    submitWithEnter(event) {
      this.disableNextButton = true;
      this.$emit('change', event.target.value);

      this.goToNextQuestion();
    },

    async goToNextQuestion() {
      this.disableNextButton = true;
      this.isBlurred = true;

      await this.$nextTick();

      if (this.fieldHasError) {
        this.disableNextButton = false;
        return;
      }

      this.$emit('go-to-next-question');
    },

    getKeyboardKey(index) {
      // https://stackoverflow.com/questions/22624379/how-to-convert-letters-to-numbers-with-javascript
      return String.fromCharCode(97 + index);
    },

    getIndexFromKey(keyString) {
      return keyString.charCodeAt(0) - 97;
    },

    bindEventListeners() {
      document.addEventListener('keydown', this.handleKeydownEvent);
    },

    generateSafeId(fieldQuestionId, optionId, suffix) {
      return kebabCase(`${fieldQuestionId}-${optionId}-${suffix}`);
    },

    unbindEventListeners() {
      document.removeEventListener('keydown', this.handleKeydownEvent);
    },
    getFieldQuestionId() {
      return this.fieldQuestionId;
    },

    handleKeydownEvent() {
      // NOTE: each component can implement this method if it wishes to respond to keyboard input
      // see src/components/mixins/keyboard-shortcut-mixin.js
      // and src/components/fields/dynamic/multi-choice-image-field.vue
    },
  },
};
